import { FocusEventHandler, MouseEventHandler, TouchEventHandler, useCallback, useRef } from 'react';
import { captureException } from '@sentry/react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

import { authorizedAxiosInstance } from '@/clients';
import {
    dashboardService,
    folderService,
    importedEntityService,
    investmentService,
    listService,
    memoService,
    pipelineService,
} from '@/services';
import { Dashboard, FMPCompany, Folder, ImportedCompany, Investment, List, Memo, Pipeline } from '@/types';

export const DETAIL_PREFETCH_STALE_TIME = 5 * 60 * 1000;

type IntentPrefetchHandlers = {
    onFocus: FocusEventHandler<HTMLElement>;
    onMouseEnter: MouseEventHandler<HTMLElement>;
    onTouchStart: TouchEventHandler<HTMLElement>;
};

export type PrefetchableWorkspaceResourceType =
    | 'dashboards'
    | 'folders'
    | 'investments'
    | 'lists'
    | 'memos'
    | 'pipelines';

type DetailQueryOptions<TEntity> = {
    queryKey: readonly unknown[];
    queryFn: () => Promise<TEntity>;
    staleTime: number;
};

export const getPrefetchedDetailQueryData = <TEntity>(queryClient: QueryClient, options: DetailQueryOptions<TEntity>) =>
    queryClient.getQueryData(options.queryKey) as TEntity | undefined;

export const fetchPrefetchedDetailQuery = <TEntity>(queryClient: QueryClient, options: DetailQueryOptions<TEntity>) =>
    queryClient.fetchQuery(options as any) as Promise<TEntity>;

const prefetchDetailQuery = <TEntity>(queryClient: QueryClient, options: DetailQueryOptions<TEntity>) =>
    queryClient.prefetchQuery(options as any);

export const workspaceResourceQueryKeys = {
    dashboards: (id: string) => ['dashboard', id] as const,
    folders: (id: string) => ['folder', id] as const,
    investments: (id: string) => ['investment', id] as const,
    lists: (id: string) => ['list', id] as const,
    memos: (id: string) => ['memo', id] as const,
    pipelines: (id: string) => ['pipeline', id] as const,
};

export const workspaceResourceDetailQueryOptions = {
    dashboards: (id: string): DetailQueryOptions<Dashboard> => ({
        queryKey: workspaceResourceQueryKeys.dashboards(id),
        queryFn: () => dashboardService.getById(id),
        staleTime: DETAIL_PREFETCH_STALE_TIME,
    }),
    folders: (id: string): DetailQueryOptions<Folder> => ({
        queryKey: workspaceResourceQueryKeys.folders(id),
        queryFn: () => folderService.getById(id),
        staleTime: DETAIL_PREFETCH_STALE_TIME,
    }),
    investments: (id: string): DetailQueryOptions<Investment> => ({
        queryKey: workspaceResourceQueryKeys.investments(id),
        queryFn: () => investmentService.getById(id),
        staleTime: DETAIL_PREFETCH_STALE_TIME,
    }),
    lists: (id: string): DetailQueryOptions<List> => ({
        queryKey: workspaceResourceQueryKeys.lists(id),
        queryFn: () => listService.getById(id),
        staleTime: DETAIL_PREFETCH_STALE_TIME,
    }),
    memos: (id: string): DetailQueryOptions<Memo> => ({
        queryKey: workspaceResourceQueryKeys.memos(id),
        queryFn: () => memoService.getById(id),
        staleTime: DETAIL_PREFETCH_STALE_TIME,
    }),
    pipelines: (id: string): DetailQueryOptions<Pipeline> => ({
        queryKey: workspaceResourceQueryKeys.pipelines(id),
        queryFn: () => pipelineService.getById(id),
        staleTime: DETAIL_PREFETCH_STALE_TIME,
    }),
};

export const searchCompanyQueryKeys = {
    profile: (tickerSymbol: string) => ['search', 'companies', normalizeTickerSymbol(tickerSymbol), 'profile'] as const,
    overviewAnalysis: (tickerSymbol: string) =>
        ['search', 'companies', normalizeTickerSymbol(tickerSymbol), 'overview-analysis'] as const,
};

export const importedEntityQueryKeys = {
    detail: (entityId: string) => ['search', 'importedEntities', entityId] as const,
};

const normalizeTickerSymbol = (tickerSymbol: string) => tickerSymbol.trim().toUpperCase();

const fetchSearchCompanyProfile = async (tickerSymbol: string) => {
    const normalizedTickerSymbol = normalizeTickerSymbol(tickerSymbol);
    const response = await authorizedAxiosInstance.get(`/api/vendors/fmp/profile/enriched/${normalizedTickerSymbol}`);

    return (response.data?.[0] ?? null) as FMPCompany | null;
};

const fetchSearchCompanyOverviewAnalysis = async (tickerSymbol: string) => {
    const normalizedTickerSymbol = normalizeTickerSymbol(tickerSymbol);
    const response = await authorizedAxiosInstance.get(
        `/api/vendors/fmp/financials/analysis/${normalizedTickerSymbol}/all`,
    );

    return response.data;
};

export const searchCompanyDetailQueryOptions = {
    profile: (tickerSymbol: string): DetailQueryOptions<FMPCompany | null> => ({
        queryKey: searchCompanyQueryKeys.profile(tickerSymbol),
        queryFn: () => fetchSearchCompanyProfile(tickerSymbol),
        staleTime: DETAIL_PREFETCH_STALE_TIME,
    }),
    overviewAnalysis: (tickerSymbol: string): DetailQueryOptions<Record<string, unknown>> => ({
        queryKey: searchCompanyQueryKeys.overviewAnalysis(tickerSymbol),
        queryFn: () => fetchSearchCompanyOverviewAnalysis(tickerSymbol),
        staleTime: DETAIL_PREFETCH_STALE_TIME,
    }),
};

export const importedEntityDetailQueryOptions = {
    detail: (entityId: string): DetailQueryOptions<ImportedCompany> => ({
        queryKey: importedEntityQueryKeys.detail(entityId),
        queryFn: () => importedEntityService.getById(entityId),
        staleTime: DETAIL_PREFETCH_STALE_TIME,
    }),
};

export const prefetchWorkspaceResourceDetail = (
    queryClient: QueryClient,
    resourceType: PrefetchableWorkspaceResourceType,
    id?: string,
) => {
    if (!id) {
        return Promise.resolve();
    }

    return prefetchDetailQuery(
        queryClient,
        workspaceResourceDetailQueryOptions[resourceType](id) as DetailQueryOptions<any>,
    );
};

export const prefetchSearchCompanyDetail = (queryClient: QueryClient, tickerSymbol?: string) => {
    if (!tickerSymbol) {
        return Promise.resolve();
    }

    return Promise.all([
        prefetchDetailQuery(queryClient, searchCompanyDetailQueryOptions.profile(tickerSymbol)),
        prefetchDetailQuery(queryClient, searchCompanyDetailQueryOptions.overviewAnalysis(tickerSymbol)),
    ]);
};

export const prefetchImportedEntityDetail = (queryClient: QueryClient, entityId?: string) => {
    if (!entityId) {
        return Promise.resolve();
    }

    return prefetchDetailQuery(queryClient, importedEntityDetailQueryOptions.detail(entityId));
};

const useIntentPrefetch = (prefetchKey?: string, prefetch?: () => Promise<unknown> | void): IntentPrefetchHandlers => {
    const prefetchedKeyRef = useRef<string>();

    const runPrefetch = useCallback(() => {
        if (!prefetchKey || !prefetch || prefetchedKeyRef.current === prefetchKey) {
            return;
        }

        prefetchedKeyRef.current = prefetchKey;

        Promise.resolve(prefetch()).catch(captureException);
    }, [prefetch, prefetchKey]);

    return {
        onFocus: runPrefetch,
        onMouseEnter: runPrefetch,
        onTouchStart: runPrefetch,
    };
};

export const useWorkspaceResourceDetailPrefetch = (
    resourceType: PrefetchableWorkspaceResourceType,
    id?: string,
): IntentPrefetchHandlers => {
    const queryClient = useQueryClient();

    return useIntentPrefetch(
        id ? `${resourceType}:${id}` : undefined,
        useCallback(
            () => prefetchWorkspaceResourceDetail(queryClient, resourceType, id),
            [id, queryClient, resourceType],
        ),
    );
};

export const useSearchCompanyDetailPrefetch = (tickerSymbol?: string): IntentPrefetchHandlers => {
    const queryClient = useQueryClient();
    const normalizedTickerSymbol = tickerSymbol ? normalizeTickerSymbol(tickerSymbol) : undefined;

    return useIntentPrefetch(
        normalizedTickerSymbol ? `search-company:${normalizedTickerSymbol}` : undefined,
        useCallback(
            () => prefetchSearchCompanyDetail(queryClient, normalizedTickerSymbol),
            [normalizedTickerSymbol, queryClient],
        ),
    );
};

export const useImportedEntityDetailPrefetch = (entityId?: string): IntentPrefetchHandlers => {
    const queryClient = useQueryClient();

    return useIntentPrefetch(
        entityId ? `imported-entity:${entityId}` : undefined,
        useCallback(() => prefetchImportedEntityDetail(queryClient, entityId), [entityId, queryClient]),
    );
};
